import * as THREE from 'three';

// ---------------------------------------------------------------------------
// Weapon Wear Shader
// ---------------------------------------------------------------------------
// Injects procedural wear into MeshStandardMaterial via onBeforeCompile.
// The wear effect uses layered hash-based noise for organic paint removal,
// exposing a darker base-metal color underneath.
//
// Wear float range: 0.0 (factory new) to 1.0 (battle-scarred).
// At 0.0: no visible wear — early return, no shader modification.
// At ~0.5: moderate scratches and exposed edges.
// At 1.0: most paint removed, heavy base-metal exposure.
// ---------------------------------------------------------------------------

/** GLSL chunk injected before dithering in the fragment shader. */
const WEAR_FRAGMENT_CHUNK = /* glsl */ `
// --- Weapon Wear ---
{
  // Multi-octave hash noise for organic wear pattern
  // L23: Use higher UV multiplier (43.0) to reduce visible noise tiling/repetition
  vec2 wUV = vUv * 43.0;

  // Hash-based pseudo-noise (no texture lookup required)
  float h1 = fract(sin(dot(wUV, vec2(127.1, 311.7))) * 43758.5453);
  float h2 = fract(sin(dot(wUV * 2.17 + 7.31, vec2(269.5, 183.3))) * 43758.5453);
  float h3 = fract(sin(dot(wUV * 4.41 + 3.87, vec2(419.2, 371.9))) * 43758.5453);

  // Combine octaves: large blotches + medium detail + fine scratches
  float noise = h1 * 0.5 + h2 * 0.3 + h3 * 0.2;

  // Edge-aware wear: areas with higher noise values wear first
  float wearThreshold = uWearFloat;
  float edgeSoftness = 0.12;
  float wearMask = smoothstep(wearThreshold - edgeSoftness, wearThreshold + edgeSoftness, noise);

  // Secondary scratch pattern for fine detail
  float scratch = fract(sin(dot(wUV * 16.0, vec2(12.9898, 78.233))) * 43758.5453);
  float scratchMask = step(1.0 - uWearFloat * 0.3, scratch);

  // Combine broad wear + fine scratches
  float totalWear = max(wearMask, scratchMask * 0.6) * uWearFloat;

  // Base metal color (gunmetal gray exposed under paint)
  vec3 baseMetal = vec3(0.45, 0.42, 0.40);

  // Darken edges where paint is chipping
  vec3 edgeDarken = vec3(0.25, 0.22, 0.20);
  float edgeRing = smoothstep(wearThreshold - edgeSoftness * 2.0, wearThreshold - edgeSoftness, noise)
                 - smoothstep(wearThreshold - edgeSoftness, wearThreshold, noise);
  edgeRing *= uWearFloat;

  // Apply wear: blend painted surface → edge darkening → base metal
  gl_FragColor.rgb = mix(gl_FragColor.rgb, edgeDarken, edgeRing * 0.4);
  gl_FragColor.rgb = mix(gl_FragColor.rgb, baseMetal, totalWear);

  // Increase roughness in worn areas (bare metal is rougher than paint)
  // This is a visual approximation since we're post-lighting
  gl_FragColor.rgb = mix(gl_FragColor.rgb, gl_FragColor.rgb * 0.92, totalWear);
}
`;

/**
 * Apply procedural wear effect to a MeshStandardMaterial.
 *
 * Injects shader code via `onBeforeCompile` that blends between the
 * skin's painted surface and an exposed base-metal color based on
 * the `wearFloat` uniform. The injected GLSL multiplies the wear
 * contribution by `uWearFloat`, so passing 0 produces no visual change
 * while still keeping a live uniform handle available — call
 * {@link setWearFloat} later to mutate wear without recompiling.
 *
 * The compiled shader uniform is stored on `material.userData.wearUniform`
 * once the material is first rendered.
 *
 * @param material - The material to modify (must be MeshStandardMaterial).
 * @param wearFloat - Initial wear amount from 0.0 (factory new) to 1.0 (battle-scarred).
 */
export function applyWearShader(
  material: THREE.MeshStandardMaterial,
  wearFloat: number,
): void {
  const clampedWear = Math.min(Math.max(wearFloat, 0.0), 1.0);

  // Box the wear value so we can mutate it via setWearFloat() before the
  // shader has compiled — the shader picks up this same reference at
  // compile time, so later writes to `userData.wearUniform.value` propagate.
  const wearUniform = { value: clampedWear };
  material.userData.wearUniform = wearUniform;

  // Store previous callback so we don't clobber other onBeforeCompile hooks
  const previousCallback = material.onBeforeCompile;

  material.onBeforeCompile = (shader, renderer) => {
    // Run any existing onBeforeCompile first
    if (previousCallback) {
      previousCallback.call(material, shader, renderer);
    }

    // Bind the live uniform reference — subsequent `setWearFloat` calls
    // mutate `wearUniform.value`, which Three.js reads directly each frame.
    shader.uniforms.uWearFloat = wearUniform;

    // Declare uniform in fragment shader header
    shader.fragmentShader = shader.fragmentShader.replace(
      'void main() {',
      'uniform float uWearFloat;\nvoid main() {',
    );

    // Inject wear calculation just before dithering (post-lighting)
    shader.fragmentShader = shader.fragmentShader.replace(
      '#include <dithering_fragment>',
      WEAR_FRAGMENT_CHUNK + '\n#include <dithering_fragment>',
    );
  };

  // Mark material as needing recompilation
  material.needsUpdate = true;

  // The cache key only flags presence of the wear shader; the exact wear
  // value lives in a uniform, so distinct floats reuse the same program
  // and `setWearFloat` updates take effect without a recompile.
  const prevCacheKey = material.customProgramCacheKey;
  material.customProgramCacheKey = () => {
    const base = typeof prevCacheKey === 'function' ? prevCacheKey() : '';
    return base + '_wear';
  };
}

/**
 * Update the wear amount on a material that previously had
 * {@link applyWearShader} applied. No-op if the material has no wear uniform.
 *
 * @param material - The material to update.
 * @param wearFloat - New wear amount (clamped to [0, 1]).
 */
export function setWearFloat(
  material: THREE.MeshStandardMaterial,
  wearFloat: number,
): void {
  const uniform = material.userData?.wearUniform as { value: number } | undefined;
  if (!uniform) return;
  uniform.value = Math.min(Math.max(wearFloat, 0.0), 1.0);
}
